import type { History } from 'history';
import URL, { qs } from 'url-parse';

import type { CasinoLaunchPayload } from '@swf/components/CasinoGameLaunch/CasinoGameLaunch';
import { zoneStyles } from '@swf/components/StarsCrm/StarsCrm';
import { gameEntity } from '@swf/core/analytics';
import { ResolverTag } from '@swf/core/enums';
import type { Modal } from '@swf/core/models/modal';
import { dispatch, getState, select } from '@swf/core/store';
import type { CasinoTable } from '@swf/core/types/CasinoGame';
import { closeWindow, getWindow, launchGame, searchRequest } from '@swf/core/utils/casino';

import { tryParse } from '../api';

import type { Handler, Path } from './matchRoute';

// TODO refactor away from central dispatch
const enqueue = (modal: Modal) => {
  dispatch.modal.close('/stars-crm');
  dispatch.modal.enqueue(modal);
};

const nameLookup = new Map([
  ['cardtable', 'card-and-table'],
  ['gameshows', 'game-shows'],
  ['vegasslots', 'vegas'],
  ['baccaratandsicbo', 'baccarat']
]);

const getName = (name: string) => nameLookup.get(name) || name;

export const pageHandler: Handler = ({ params }, history, tracker) => {
  tracker?.('navigate');
  const { name, tab } = params;
  history.push({
    pathname: `/${getName(name) || ''}${tab ? `/${getName(tab)}` : ''}`
  });
};

export const signupHandler: Handler = (_, __, tracker) => {
  tracker?.('sign_up');
  enqueue({ path: '/auth/registration' });
};

export const authHandler: Handler = (_, __, tracker) => {
  tracker?.('login');
  enqueue({ path: '/auth/login' });
};

export const modalZoneHandler: Handler = ({ params }, _, tracker) => {
  const { zone } = params;
  if (zone) {
    tracker?.('open_crm_modal');
    enqueue({
      path: '/stars-crm',
      locationState: {
        zoneId: zone.toUpperCase(),
        scrolling: 'yes',
        className: zoneStyles.MODALZONE,
        componentType: 'Single'
      }
    });
  }
};

export const accountHandler: Handler = ({ url: path }, _, tracker) => {
  tracker?.('open_account');
  if (select.session.isAuthenticated(getState())) {
    enqueue({ path });
  } else {
    enqueue({ path: '/auth/login' });
  }
};

export const gameLaunchHandler: Handler = async ({ params }, history, tracker) => {
  const { gameTableId, name } = params;
  if (!gameTableId) {
    return;
  }
  if (name && history) {
    pageHandler({ params } as Path, history);
  }
  let windowRef;
  const rootState = getState();
  if (select.session.isAuthenticated(getState())) {
    windowRef = getWindow({ table: { id: parseInt(gameTableId, 10) } as CasinoTable, rootState });
  }
  const results = await searchRequest({ query: gameTableId, paramCode: 'tabletypeid' }, rootState);

  if (results.length) {
    const [game] = results;
    const playTypes = Object.keys(game.tables);
    if (playTypes.length === 1) {
      tracker?.('launch_game', [gameEntity(game)]);
      dispatch.casino.launch({ game, table: game.tables[playTypes[0]][0], windowRef });
    }
  } else {
    closeWindow(windowRef, select.device.isMobile(rootState));
  }
};

export const gameInternalLaunchHandler: Handler = ({ params }, history) => {
  const id = params.type === 'race' ? params.id : tryParse(params.id);
  const { code, raceId, fsb } = qs.parse(params[1]) as CasinoLaunchPayload;
  const race = params.type === 'race';
  launchGame({ id, code, raceId, race, fsb });
};

export const casinoC2LHandler: Handler = (path, history) => {
  history.push(`/casino/${path.url}`);
};

export const wslHandler: Handler = (path, history) =>
  path.params.gameTableId ? gameLaunchHandler(path, history) : pageHandler(path, history);

// TODO: refactor to use Handler
export const launchICEHandler = (icegame: string, { params: { id } }: Path, history: History) => {
  history.push(`/account/ice/${icegame}/${id}`);
};

export const launchWindowHandler: Handler = async ({ params: { resolverTag } }) => {
  const resolverTagKey = Object.keys(ResolverTag).find((key) => ResolverTag[key] === resolverTag);

  // do nothing if ResolverTag not found
  if (resolverTagKey) {
    await dispatch.resolver.fetchTag({ tag: resolverTag });
    const rootState = getState();
    const { url } = select.resolver.getTag(rootState, resolverTag);
    const { locale } = rootState;
    const parsed = new URL(url, true);
    parsed.query.language = locale.toString();
    window.open(parsed.toString(), resolverTag, 'scrollbars=yes,resizable=no');
  }
};
